import React, { useEffect, useMemo, useState } from "react";
import api from "../api/http";
import { FaDraftingCompass, FaTools, FaCheckCircle, FaClipboardCheck, FaArrowRight, FaLeaf, FaShieldAlt, FaRegClock, FaChartLine, FaCertificate, FaHardHat, FaSolarPanel, } from "react-icons/fa";

/** Presentational helper */
function FeatureItem({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 w-9 h-9 grid place-items-center rounded-lg bg-cyan-50 ring-1 ring-cyan-200">
        <Icon className="text-cyan-600" />
      </span>
      <div>
        <div className="font-semibold text-neutral-900">{title}</div>
        <p className="text-sm text-neutral-600">{text}</p>
      </div>
    </div>
  );
}

function CheckItem({ children }) {
  return (
    <div className="flex items-center gap-2">
      <FaCheckCircle className="text-cyan-600" />
      <span className="text-sm text-neutral-700">{children}</span>
    </div>
  );
}

/** Years since a given start date*/
function yearsSince(startISO) {
  const start = new Date(startISO);
  const today = new Date();
  let years = today.getFullYear() - start.getFullYear();
  const m = today.getMonth() - start.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < start.getDate())) years--;
  return years < 0 ? 0 : years;
}

/** Normalize counts from common API response */
function normalizeCounts(data) {
  if (!data) return null;

  // flat shapes
  if (typeof data.ongoing === "number" && typeof data.completed === "number") {
    return { ongoing: data.ongoing, completed: data.completed };
  }
  if (typeof data.ongoing === "number" && typeof data.finished === "number") {
    return { ongoing: data.ongoing, completed: data.finished };
  }
  if (
    typeof data.ongoing_count === "number" &&
    (typeof data.completed_count === "number" || typeof data.finished_count === "number")
  ) {
    return {
      ongoing: data.ongoing_count,
      completed:
        typeof data.completed_count === "number"
          ? data.completed_count
          : data.finished_count,
    };
  }

  if (data?.data) return normalizeCounts(data.data);
  return null;
}

/** Try to infer count */
function inferListCount(resp) {
  if (!resp) return 0;
  const { data, headers } = resp;

  // Some APIs place total in a header
  const headerTotal = headers?.["x-total-count"] || headers?.["X-Total-Count"];
  if (headerTotal) return parseInt(headerTotal, 10) || 0;

  if (data?.meta?.total != null) return Number(data.meta.total) || 0;
  if (data?.total != null) return Number(data.total) || 0;
  if (Array.isArray(data)) return data.length;
  if (Array.isArray(data?.data)) return data.data.length;

  return 0;
}

/** Fetch counts */
async function fetchProjectCounts() {
  for (const path of ["projects/stats", "projects/counts"]) {
    try {
      const res = await api.get(path);
      const out = normalizeCounts(res.data);
      if (out) return out;
    } catch {
    }
  }

  // Fallback precise counts
  try {
    const [ongoRes, compRes] = await Promise.all([
      api.get("projects/count", { params: { status: "ongoing" } }),
      api.get("projects/count", { params: { status: "finished" } }),
    ]);
    const ongoing =
      typeof ongoRes?.data?.count === "number" ? ongoRes.data.count : inferListCount(ongoRes);
    const completed =
      typeof compRes?.data?.count === "number" ? compRes.data.count : inferListCount(compRes);
    return { ongoing, completed };
  } catch {
  }

  // Last resort
  try {
    const [ongoRes, compRes] = await Promise.all([
      api.get("projects", { params: { status: "ongoing", per_page: 1 } }),
      api.get("projects", { params: { status: "finished", per_page: 1 } }),
    ]);
    return { ongoing: inferListCount(ongoRes), completed: inferListCount(compRes) };
  } catch (err) {
    console.error("Failed to fetch project counts", err);
    return { ongoing: null, completed: null };
  }
}

/* Component */

export default function AboutUs() {
  const YEARS_START = "2009-10-02";
  const yearsExp = useMemo(() => yearsSince(YEARS_START), []);

  const [counts, setCounts] = useState({
    ongoing: null,
    completed: null,
    loading: true,
  });

  useEffect(() => {
    let canceled = false;
    (async () => {
      const result = await fetchProjectCounts();
      if (!canceled) setCounts({ ...result, loading: false });
    })();
    return () => {
      canceled = true;
    };
  }, []);

  const fmt = (n) => (typeof n === "number" && !Number.isNaN(n) ? `${n}+` : "—");

  return (
    <main className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="h-[420px] md:h-[460px] bg-cover bg-center"
          style={{ backgroundImage: "url('/images/About.jpg')" }}
        />
        <div className="absolute inset-0 bg-black/55" />
      {/* content */}
        <div className="absolute inset-0">
          <div className="max-w-7xl mx-auto h-full px-4 md:px-6 grid gap-6 md:grid-cols-2">
            {/* Headline + cta */}
            <div className="flex items-center">
              <div className="text-white max-w-2xl">
                <h1 className="mt-2 text-4xl md:text-5xl font-extrabold leading-tight">
                  About us
                </h1>
                <p className="mt-4 text-lg text-white/90">
                  Building with integrity since our founding. Guided by
                  innovation, teamwork, and sustainability, we deliver projects
                  that stand the test of time.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-md bg-cyan-400 text-black font-semibold shadow-md hover:bg-cyan-300 transition"
                >
                  GET A FREE QUOTE
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 text-center">
            About us
          </h2>
          <div className="w-20 h-1 bg-cyan-500 mx-auto mt-3" />

          {/* Company Intro (About Us) */}
          <div className="mt-8 grid gap-8 md:grid-cols-2 items-center">
            {/* Image */}
            <div>
              <img
                src="images/about1.jpg"
                alt="PERA Engineering"
                className="rounded-lg shadow-lg w-full object-cover"
              />
            </div>

            {/* Content */}
            <div>
              <p className="text-neutral-700 leading-relaxed">
                Our company, <strong>Pera Engineering (Pvt) Ltd</strong>, was founded on{" "}
                <strong>2nd October 2009</strong> at{" "}
                No. 14/3A, Suvisddarama Road, Colombo-06 by{" "}
                <strong>Mr. A. Rajamohan</strong> and <strong>Mrs. R. Sanghirtha</strong>. 

{/* <p className="text-neutral-700 leading-relaxed">
  Our company, <strong>Pera Engineering (Pvt) Ltd</strong>, was originally established on 
  <strong> 2nd October 2009</strong> under the name <strong>Pera Engineering Construction (Pvt) Ltd</strong> 
  at No. 14/3A, Suvisddarama Road, Colombo-06 by <strong>Mr. A. Rajamohan</strong> and 
  <strong> Mrs. R. Sanghirtha</strong>. The company was later renamed to 
  <strong> Pera Engineering (Pvt) Ltd</strong> on <strong>6th November 2020</strong>. */}

                The name <strong>PERA</strong> stands for                       <span
                        className="text-[#2e3192] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        P
                      </span>
                      <span
                        className="text-gray-700 font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        lanning,
                      </span>{" "}
                      {/* E */}
                      <span
                        className="text-[#8d1b1e] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        E
                      </span>
                      <span
                        className="text-gray-700 font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        valuation,
                      </span>{" "}
                      {/* R */}
                      <span
                        className="text-[#2e3192] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        R
                      </span>
                      <span
                        className="text-gray-700 font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        esearch,
                      </span>{" "}
                      {/* Normal "and" (no bold, no special font) */}
                      <span className="text-gray-700" style={{ fontFamily: "inherit" }}>
                        and
                      </span>{" "}
                      {/* A */}
                      <span
                        className="text-[#8d1b1e] font-bold"
                        style={{
                          fontFamily: '"Monotype Corsiva", cursive',
                          fontSize: "1.3em",
                          lineHeight: "1",
                        }}
                      >
                        A
                      </span>
                      <span
                        className="text-gray-700 font-bold"
                        style={{ fontFamily: '"Monotype Corsiva", cursive' }}
                      >
                        nalysis
                      </span>, 
                reflecting our commitment to excellence in every project.
              </p>

              <p className="text-neutral-700 leading-relaxed mt-4">
                With the vision of <strong>“We convert land into landmark,”</strong> we deliver a wide range of 
                construction services including:
              </p>

              <ul className="list-disc list-inside text-neutral-700 leading-relaxed mt-2 space-y-1">
                <li>
                  <strong>Design & Build:</strong> From concept to IFC drawings covering buildings, 
                  shopping complexes, school buildings, churches, and Hindu temples.
                </li>
                <li>
                  <strong>Remodels & Renovations:</strong> Large-scale modernisation for commercial, 
                  educational, and residential complexes. Project value: LKR 10M and above.
                </li>
                <li>
                  <strong>Site Improvements:</strong> Earthworks, drainage, utilities, and landscaping 
                  built to heavy-duty commercial standards. Project value: LKR 10M and above.
                </li>
              </ul>

              <p className="text-neutral-700 leading-relaxed mt-4">
                Over the years, we’ve built a strong reputation for delivering projects with engineering precision, 
                innovative design, and uncompromising quality.
              </p>
            </div>
          </div>

          {/* Leadership Section */}
          <section className="mt-10">
            <div className="text-center mb-12">
              <p className="uppercase text-base sm:text-lg font-bold tracking-widest text-sky-600">
                Our Leadership
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold">
                Meet the visionaries who founded
                <br className="hidden md:block" /> and continue to inspire PERA Engineering
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
              {/* CEO 1 */}
              <div className="bg-white rounded-2xl shadow-lg border border-sky-100 p-8 text-center hover:shadow-2xl hover:-translate-y-2 transform transition duration-300">
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <img
                    src="/images/CEO-1.jpg"
                    alt="A. Rajamohan"
                    className="w-48 h-48 rounded-full object-cover shadow-lg border-4 border-sky-200"
                  />
                  <span className="absolute bottom-2 right-8 bg-sky-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow">
                    Managing Director
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Mr. A. Rajamohan</h3>
                <p className="text-sky-600 font-semibold mb-2">Founder & Managing Director</p>
                <p className="text-gray-600 text-base leading-relaxed">
                  🎓 B.Sc. in Civil Engineering <br className="hidden md:block" />
                  🏗️ Over 25 years’ expertise in Construction.  
                </p>
              </div>

              {/* CEO 2 */}
              <div className="bg-white rounded-2xl shadow-lg border border-sky-100 p-8 text-center hover:shadow-2xl hover:-translate-y-2 transform transition duration-300">
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <img
                    src="/images/CEO-2.jpg"
                    alt="R. Sanghirtha"
                    className="w-48 h-48 rounded-full object-cover shadow-lg border-4 border-sky-200"
                  />
                  <span className="absolute bottom-2 right-8 bg-sky-600 text-white px-11 py-1 rounded-full text-xs font-semibold shadow">
                    Director
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Mrs. R. Sanghirtha</h3>
                <p className="text-sky-600 font-semibold mb-2">Co-Founder & Director</p>
                <p className="text-gray-600 text-base leading-relaxed">
                  📊 Skilled in planning, cost control, and Accounting.  
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* SERVICES BAND */}
      <section id="services" className="relative">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-sky-50" />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="uppercase text-base sm:text-lg font-bold tracking-widest text-sky-600">
              Our Services
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-extrabold">
              High-Quality Construction Solutions
              <br className="hidden md:block" /> for Residential & Industrial Projects
            </h2>
          </div>

          {/* service cards */}
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Card 1 – Design & Build */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="p-7">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaDraftingCompass className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Design &amp; Build</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We offer a single contract delivery model that takes your project from 
                  the initial concept stage all the way to IFC (Issued for Construction) drawings.
                </p>
                <ul className="mt-3 text-sm text-neutral-700 list-disc pl-5 space-y-1">
                  <li>Buildings</li>
                  <li>Shopping Complexes</li>
                  <li>School Buildings</li>
                  <li>Churches</li>
                  <li>Hindu Temples</li>
                </ul>
              </div>
            </article>

            {/* Card 2 – Remodels & Renovations */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="p-7">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaTools className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Remodel &amp; Renovations</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We handle complete upgrades, adaptive reuse, and modernisation 
                  projects to keep your facilities up to date with today’s needs. 
                  This service is especially suited for large-scale commercial 
                  buildings, schools, and residential complexes.
                </p>
                <p className="mt-2 font-semibold text-sky-700">
                  Project value: LKR 10M and above.
                </p>
              </div>
            </article>

            {/* Card 3 – Construction Consult */}
            <article className="rounded-2xl border bg-white shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="p-7">
                <div className="mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-sky-50 ring-1 ring-sky-200 flex items-center justify-center">
                    <FaClipboardCheck className="text-sky-600 text-4xl md:text-5xl" />
                  </div>
                </div>
                <h3 className="text-xl font-bold">Construction Consult</h3>
                <p className="mt-2 text-base text-neutral-600">
                  We provide professional advisory services that cover feasibility studies, 
                  cost estimating, value engineering, and scheduling. 
                  Our guidance is customized to help you optimise investments, improve efficiency, 
                  and stay fully compliant with both local and international standards.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid gap-10 lg:grid-cols-3">
          {/* Card */}
          <div className="lg:col-span-1">
            <div className="bg-neutral-50 border rounded-2xl p-6 md:p-8 shadow-sm">
              <h3 className="text-xl md:text-2xl font-extrabold">
                Why choose us
              </h3>

              {/* Primary features */}
              <div className="mt-6 space-y-5">
                <FeatureItem
                  icon={FaLeaf}
                  title="Eco-Friendly Construction"
                  text="Sustainable materials, waste reduction, and energy-efficient designs."
                />
                <FeatureItem
                  icon={FaShieldAlt}
                  title="Safety First"
                  text="Zero-harm culture with tool-box talks and trained supervisors."
                />
                <FeatureItem
                  icon={FaRegClock}
                  title="On-Time Delivery"
                  text="Granular scheduling, clear milestones, and daily site reporting."
                />
                <FeatureItem
                  icon={FaChartLine}
                  title="Cost Control"
                  text="Transparent BOQs, value engineering, and budget tracking."
                />
              </div>

              {/* Mini checklist */}
              <div className="mt-7 grid grid-cols-2 gap-3">
                <CheckItem>ISO-aligned QA/QC</CheckItem>
                <CheckItem>Skilled workforce</CheckItem>
                <CheckItem>Design–Build ready</CheckItem>
                <CheckItem>Client Care</CheckItem>
              </div>

              {/* CTA row */}
              <div className="mt-8 flex items-center gap-3">
                <a
                  href="/projects"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-neutral-300 font-semibold hover:bg-neutral-100 transition"
                >
                  View Projects
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-cyan-600 text-white font-semibold shadow hover:bg-cyan-700 transition"
                >
                  Get Quote <FaArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Image + overlay badges + metrics bar */}
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-2xl group">
              <img
                src="/images/about1.jpg"
                alt="Signature infrastructure"
                className="w-full h-[360px] md:h-[420px] object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />

              {/* Top-left overlay badges */}
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 backdrop-blur ring-1 ring-white/70 text-sm font-semibold">
                  <FaCertificate className="text-cyan-600" /> ISO-aligned QA/QC
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 backdrop-blur ring-1 ring-white/70 text-sm font-semibold">
                  <FaHardHat className="text-cyan-600" /> Safety Compliant
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 backdrop-blur ring-1 ring-white/70 text-sm font-semibold">
                  <FaSolarPanel className="text-cyan-600" /> Energy Efficient
                </span>
              </div>

              {/* Bottom gradient caption */}
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/60 via-black/20 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <div className="text-lg md:text-xl font-bold">
                      Infrastructure That Lasts
                    </div>
                    <div className="text-white/80 text-sm">
                      Bridges • Warehouses • Houses • Coastal works
                    </div>
                  </div>
                  <a
                    href="/contact"
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-black font-semibold hover:bg-cyan-400 transition"
                  >
                    Talk to Us <FaArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* Metrics ribbon */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {/* Years Experience — auto from 2009-10-02 */}
              <div className="rounded-xl bg-cyan-500/10 border border-cyan-200 p-4">
                <div className="text-3xl font-extrabold text-cyan-700">
                  {fmt(yearsExp)}
                </div>
                <div className="text-sm font-semibold text-cyan-800/80">
                  Years Experience
                </div>
              </div>

              {/* Ongoing Projects */}
              <div className="rounded-xl bg-neutral-200 border border-neutral-300 p-4">
                <div className="text-3xl font-extrabold text-cyan-600">
                  {fmt(counts.ongoing)}
                </div>
                <div className="text-sm font-semibold text-cyan-600">
                  Ongoing Projects
                </div>
              </div>

              {/* Completed Projects */}
              <div className="rounded-xl bg-cyan-600 text-white border border-cyan-700 p-4">
                <div className="text-3xl text-white font-extrabold">
                  {fmt(counts.completed)}
                </div>
                <div className="text-sm font-semibold text-white">
                  Completed Projects
                </div>
              </div>

              {/* Experienced Staff */}
              <div className="rounded-xl bg-neutral-700 border border-neutral-800 p-4">
                <div className="text-3xl font-extrabold text-white">50+</div>
                <div className="text-sm font-semibold text-white">
                  Experienced Staff
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}